import { useEffect, useState } from 'react';
import { Phone, Mail, MapPin, Globe, User, Building2, HandMetal as Handshake, AlertTriangle } from 'lucide-react';
import { fetchCustomerProfile } from '../api';
import type { Kundprofil as Profil } from '../api';

type Vy = 'kontakt' | 'avtal';

// Laddar kundprofilen när kunden byts. Kunden är en prop, så ett byte avbryter
// ett pågående svar i stället för att visa förra kundens uppgifter.
function useKundprofil(customerId: string) {
    const [profil, setProfil] = useState<Profil | null>(null);
    const [fel, setFel] = useState(false);
    useEffect(() => {
        let aktuell = true;
        setProfil(null);
        setFel(false);
        fetchCustomerProfile(customerId)
            .then(p => { if (aktuell) setProfil(p); })
            .catch(() => { if (aktuell) setFel(true); });
        return () => { aktuell = false; };
    }, [customerId]);
    return { profil, fel };
}

const SEK = new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 });

function Rad({ ikon, etikett, varde, href }: { ikon: React.ReactNode; etikett: string; varde: string | null; href?: string }) {
    return (
        <div className="cv-placeholder-card">
            {ikon}
            <span>{etikett}</span>
            {varde && href
                ? <a className="cv-profil-value" href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{varde}</a>
                : <span className={varde ? 'cv-profil-value' : 'cv-placeholder-value'}>{varde ?? '—'}</span>}
        </div>
    );
}

function Tomt({ ikon, text }: { ikon: React.ReactNode; text: string }) {
    return (
        <div className="cv-detail-empty-tab">
            {ikon}
            <p>{text}</p>
            <p className="cv-placeholder-note">Koppla kundens CRM-kort (taggen client) till kunden för att fylla fliken</p>
        </div>
    );
}

export function Kundprofil({ customerId, vy }: { customerId: string; vy: Vy }) {
    const { profil, fel } = useKundprofil(customerId);

    if (fel) {
        return <Tomt ikon={<AlertTriangle size={24} strokeWidth={1.5} />} text="Kunde inte hämta uppgifterna från CRM" />;
    }
    if (!profil) {
        return <div className="cv-detail-empty-tab"><p>Laddar…</p></div>;
    }

    if (vy === 'kontakt') {
        if (profil.kontakter.length === 0) {
            return <Tomt ikon={<Mail size={24} strokeWidth={1.5} />} text="Inget kundkort kopplat i CRM" />;
        }
        return (
            <div className="cv-detail-placeholder">
                {profil.kontakter.map(k => (
                    <div key={k.id} className="cv-profil-block">
                        {profil.kontakter.length > 1 && <h4 className="cv-profil-rubrik">{k.namn}</h4>}
                        <Rad ikon={<Building2 size={14} />} etikett="Bolag" varde={k.bolag ?? k.namn} />
                        <Rad ikon={<User size={14} />} etikett="Kontaktperson" varde={k.kontaktperson} />
                        <Rad ikon={<Phone size={14} />} etikett="Telefon" varde={k.telefon} href={k.telefon ? `tel:${k.telefon.replace(/\s/g, '')}` : undefined} />
                        <Rad ikon={<Mail size={14} />} etikett="E-post" varde={k.epost} href={k.epost ? `mailto:${k.epost}` : undefined} />
                        <Rad ikon={<Globe size={14} />} etikett="Webb" varde={k.webb} href={k.webb ?? undefined} />
                        <Rad ikon={<MapPin size={14} />} etikett="Adress" varde={k.adress} />
                    </div>
                ))}
                <p className="cv-placeholder-note">Från CRM-kortet · ändras under Försäljning → CRM</p>
            </div>
        );
    }

    if (profil.avtal.length === 0 && profil.affarer.length === 0) {
        return <Tomt ikon={<Handshake size={24} strokeWidth={1.5} />} text="Inga avtal registrerade" />;
    }
    return (
        <div className="cv-detail-placeholder">
            {profil.beskrivning && <p className="cv-profil-lead">{profil.beskrivning}</p>}
            {profil.affarer.length > 0 && (
                <div className="cv-profil-block">
                    <h4 className="cv-profil-rubrik">Affärer</h4>
                    {profil.affarer.map(a => (
                        <div key={a.id} className="cv-placeholder-card">
                            <Handshake size={14} />
                            <span>{a.titel ?? 'Affär'}</span>
                            <span className="cv-profil-value">
                                {[a.varde_sek != null ? SEK.format(a.varde_sek) : null, a.steg, a.pipeline].filter(Boolean).join(' · ') || '—'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
            {profil.avtal.map(a => (
                <div key={`${a.kontakt_id}-${a.etikett}`} className="cv-profil-avtal">
                    <span className="cv-profil-etikett">{a.etikett}</span>
                    <p>{a.text}</p>
                </div>
            ))}
            <p className="cv-placeholder-note">Från CRM-kortet · ändras under Försäljning → CRM</p>
        </div>
    );
}
